<template>
  <v-card flat>
    <v-card-title>
      Programmes
      <v-spacer></v-spacer>
      <v-btn
        depressed
        color="primary"
        @click="addNewLine"
        v-if="
          !isNew &&
          (formData.submittedBy === user.number || formData.submittedBy === '')
        "
      >
        <v-icon>mdi-plus</v-icon>
        add programme
      </v-btn>
    </v-card-title>

    <v-data-table
      :headers="trainingLinesHeaders"
      :items="trainingLinesList"
      hide-default-footer
    >
      <!-- Programme Code -->
      <template v-slot:item.programmeCode="{ item }">
        <v-autocomplete
          v-if="displayConditionally(item)"
          @focusout="autosaveTrainingLine"
          v-model="line.programmeCode"
          :rules="rules.required"
          :items="courses"
          :item-text="(item) => `${item.no} - ${item.description}`"
          :item-value="(item) => item.no"
          label="Select Programme"
          dense
          single-line
          class="mb-n3"
        ></v-autocomplete>
        <span v-else>{{ item.programmeCode }}</span>
      </template>

      <!-- Description -->
      <template v-slot:item.trainingDetails="{ item }">
        <v-text-field
          @focusout="autosaveTrainingLine"
          v-model="line.trainingDetails"
          v-if="displayConditionally(item)"
          label="Description"
          dense
          single-line
          class="mb-n3"
        />
        <span v-else>{{ item.trainingDetails }}</span>
      </template>

      <template v-slot:item.expenseCode="{ item }">
        <!-- Editable mode -->
        <v-autocomplete
          v-if="displayConditionally(item)"
          label="Expense Code"
          dense
          single-line
          v-model="line.trainingNeedCosts[0].expenseCode"
          @focusout="autosaveTrainingLine"
          :items="expenseCodes"
          item-value="code"
          item-text="description"
          class="mb-n3"
          :disabled="item.lineNo"
        />

        <!-- Display mode -->
        <span v-else>
          {{
            item.trainingNeedCosts && item.trainingNeedCosts.length
              ? getExpenseDescription(item)
              : ""
          }}
        </span>
      </template>

      <template v-slot:item.amountInclVATLCY="{ item }">
        <!-- Editable mode -->
        <v-text-field
          v-if="displayConditionally(item)"
          :label="`Amount (${currency})`"
          dense
          single-line
          v-model.number="line.trainingNeedCosts[0].amountExclVATLCY"
          type="number"
          @focusout="autosaveTrainingLine"
          class="mb-n3"
        ></v-text-field>

        <!-- Display mode -->
        <span v-else>
          Ksh
          {{
            item.trainingNeedCosts && item.trainingNeedCosts.length
              ? formatAmount(item.trainingNeedCosts[0].amountExclVATLCY)
              : ""
          }}
        </span>
      </template>

      <!-- Start Date -->
      <template v-slot:item.startDate="{ item }">
        <v-menu
          v-if="displayConditionally(item)"
          ref="menu"
          v-model="menu"
          :close-on-content-click="false"
          transition="scale-transition"
          offset-y
          min-width="auto"
        >
          <template v-slot:activator="{ on, attrs }">
            <v-text-field
              v-model="line.startDate"
              label="Start date"
              single-line
              v-bind="attrs"
              v-on="on"
              :rules="rules.required"
            ></v-text-field>
          </template>
          <v-date-picker
            v-model="line.startDate"
            @click:date="autosaveTrainingLine"
          />
        </v-menu>
        <span v-else>{{ getDateFormat(item.startDate) }}</span>
      </template>

      <!-- End Date -->
      <template v-slot:item.endDate="{ item }">
        <v-menu
          v-if="displayConditionally(item)"
          ref="menu2"
          v-model="menu2"
          :close-on-content-click="false"
          transition="scale-transition"
          offset-y
          min-width="auto"
        >
          <template v-slot:activator="{ on, attrs }">
            <v-text-field
              v-model="line.endDate"
              :disabled="!line.startDate"
              :rules="rules.required"
              label="End date"
              single-line
              v-bind="attrs"
              v-on="on"
            ></v-text-field>
          </template>
          <v-date-picker
            v-model="line.endDate"
            :min="line.startDate"
            @click:date="autosaveTrainingLine"
          ></v-date-picker>
        </v-menu>
        <span v-else>{{ getDateFormat(item.endDate) }}</span>
      </template>

      <!-- Actions -->
      <template v-slot:item.actions="{ item }">
        <div>
          <div v-if="displayConditionally(item)">
            <!-- Save -->
            <v-tooltip bottom v-if="item.lineNo">
              <template v-slot:activator="{ on, attrs }">
                <v-icon
                  v-bind="attrs"
                  v-on="on"
                  color="blue"
                  class="mr-3"
                  @click="saveProgramme(item)"
                >
                  mdi-content-save
                </v-icon>
              </template>
              <span>Save Programme</span>
            </v-tooltip>

            <!-- Close -->
            <v-tooltip bottom>
              <template v-slot:activator="{ on, attrs }">
                <v-icon
                  v-bind="attrs"
                  v-on="on"
                  color="red"
                  class="mr-3"
                  @click="close"
                >
                  mdi-window-close
                </v-icon>
              </template>
              <span>Close Editing</span>
            </v-tooltip>

            <!-- Delete -->
            <v-icon color="red" @click="deleteItem(item)">mdi-delete</v-icon>
          </div>

          <div v-else>
            <v-icon color="green" class="mr-3" @click="editItem(item)">
              mdi-pencil
            </v-icon>
            <v-icon color="red" @click="deleteItem(item)">mdi-delete</v-icon>
          </div>
        </div>
      </template>
    </v-data-table>
  </v-card>
</template>

<script>
import helpers from "@kinetics254/cassandra-base/utilities/helpers/helper";

export default {
  name: "Programmes",
  props: {
    formData: Object,
    isEdit: Boolean,
    isSupervisor: Boolean,
    canEdit: Boolean,
    user: Object,
    isNew: Boolean,
  },
  data() {
    return {
      menu: false,
      menu2: false,
      startDate: new Date(Date.now() - new Date().getTimezoneOffset() * 60000)
        .toISOString()
        .substr(0, 10),
      lineIndex: -1,
      line: {
        documentNo: "",
        endDate: "",
        programmeCode: "",
        startDate: "",
        trainingDetails: "",
        expenseCode: "",
        amountInclVATLCY: null,
        trainingNeedCosts: [
          {
            amountExclVATLCY: 0,
            amountInclVATLCY: 0,
            documentNo: "",
            expenseCode: "",
            trainingNeedLineNo: 0,
          },
        ],
      },
      defaultLineItem: {
        documentNo: "",
        endDate: "",
        programmeCode: "",
        startDate: "",
        trainingDetails: "",
        expenseCode: "",
        amountInclVATLCY: null,
        trainingNeedCosts: [
          {
            amountExclVATLCY: 0,
            amountInclVATLCY: 0,
            documentNo: "",
            expenseCode: "",
            trainingNeedLineNo: 0,
          },
        ],
      },
      headers: [
        { text: "Programme", value: "programmeCode", sortable: false },
        { text: "Description", value: "trainingDetails", sortable: false },
        { text: "Expense Code", value: "expenseCode", sortable: false },
        { text: "Amount", value: "amountInclVATLCY", sortable: false },
        { text: "Start Date", value: "startDate", sortable: false },
        { text: "End Date", value: "endDate", sortable: false },
        { text: "Actions", value: "actions", sortable: false },
      ],
    };
  },
  computed: {
    expenseCodes() {
      return this.$store.getters["imprest/expenseCodes"];
    },
    currency() {
      return "KES";
    },
    trainingLinesHeaders() {
      return this.headers;
    },
    trainingLinesList: {
      get() {
        return this.formData.trainingNeedLines;
      },
      set(val) {
        this.formData.trainingNeedLines = val;
      },
    },
    courses() {
      return this.$store.getters["Setup/setupGetter"]("trainingProgrammes");
    },
    rules() {
      return {
        required: [(v) => !!v || "field is required"],
      };
    },
  },
  methods: {
    displayConditionally(item) {
      if (item && this.line) {
        return (
          (item.lineNo && item.lineNo === this.line.lineNo) ||
          (item.id && item.id === this.line.id)
        );
      }
      return false;
    },

    addNewLine() {
      const addObj = {
        ...this.defaultLineItem,
        id: Date.now(),
        trainingNeedCosts: JSON.parse(
          JSON.stringify(this.defaultLineItem.trainingNeedCosts)
        ),
      };
      this.trainingLinesList.unshift(addObj);
      this.editItem(addObj);
    },

    editItem(item) {
      this.lineIndex = this.trainingLinesList.indexOf(item);
      this.line = { ...item };
    },

    autosaveTrainingLine() {
      if (this.lineIndex > -1) {
        Object.assign(this.trainingLinesList[this.lineIndex], this.line);
      }
    },

    async saveProgramme(item) {
      if (
        !this.line.trainingNeedCosts ||
        !this.line.trainingNeedCosts[0].expenseCode ||
        this.line.trainingNeedCosts[0].amountExclVATLCY === null
      ) {
        alert("Expense code and amount cannot be empty.");
        return;
      }

      try {
        this.line.documentNo = this.formData.no;

        const payload = { ...this.line };

        // Ensure nested data consistency
        const cost = payload.trainingNeedCosts[0];
        payload.trainingNeedCosts[0] = {
          ...cost,
          documentNo: payload.documentNo,
          trainingNeedLineNo: payload.lineNo || 0,
          amountExclVATLCY: Number(cost.amountExclVATLCY),
          amountInclVAT: 0,
          amountInclVATLCY: Number(cost.amountExclVATLCY),
          description: payload.trainingDetails || "",
          vat: 0,
          attendanceType: cost.attendanceType || "Yes",
        };

        console.log("final payload before saving", payload);
        await this.$store.dispatch("Training/saveProgramme", payload);

        this.close();
      } catch (error) {
        console.error("Error saving programme:", error);
        this.$alert.error("Failed to save programme.");
      }
    },

    close() {
      this.line = {};
      this.lineIndex = -1;
    },

    deleteItem(item) {
      if ("lineNo" in item) {
        const payload = {
          documentNo: item.documentNo,
          lineNo: item.lineNo,
        };
        this.$confirm.show({
          text: "Are you sure you want to remove this Item?",
          onConfirm: async () => {
            try {
              await this.$store.dispatch(
                "Training/deleteTrainingLine",
                payload
              );
              this.$emit("deleted", item);
            } catch (e) {
              //
            }
          },
        });
      } else {
        this.trainingLinesList = this.trainingLinesList.filter(
          (line) => line.id !== item.id
        );
      }
    },

    getDateFormat(date) {
      return helpers.getDateFormat(date);
    },
    getExpenseDescription(item) {
      if (!item.trainingNeedCosts || !item.trainingNeedCosts.length) return "";
      const expenseCode = item.trainingNeedCosts[0].expenseCode;
      const expense = this.expenseCodes.find((e) => e.code === expenseCode);
      return expense ? expense.description : expenseCode || "";
    },
    formatAmount(value) {
      if (!value && value !== 0) return "";
      return new Intl.NumberFormat("en-KE", {
        minimumFractionDigits: 0,
        maximumFractionDigits: 2,
      }).format(value);
    },
  },
};
</script>

<style scoped>
.v-icon {
  cursor: pointer;
}
</style>
